import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback } from 'react';
import { ClickableElement } from '@/atoms';
import { useEnsuredForwardedRef } from '@/functions';
import { medium, colors, mediumInter } from '@/styles';
import { MagnifyingGlass, Close } from '@/svgs';

/* Search Icon Size Mapping */
const searchIconSizes = {
  // Divide the designed size by 2.85 to "undo" multiplication in `MagnifyingGlass` component
  small: 12.31 / 2.85,
  medium: 14.36 / 2.85,
  large: 14.36 / 2.85,
};

/* Clear Icon Size Mapping */
const clearIconSizes = {
  // Divide the designed size by 2.85 to "undo" multiplication in `Close` component
  small: 10 / 2.85,
  medium: 12 / 2.85,
  large: 12 / 2.85,
};

/**
 * Managed search bar component
 *
 * @param {import('./ManagedSearchBar').ManagedSearchBarProps} props
 * @param {React.MutableRefObject<HTMLInputElement>} ref
 */
function ManagedSearchBar(
  { className, onChange, onFocus, onSelect, size, value, placeHolder, isRebrand },
  ref,
) {
  const inputRef = useEnsuredForwardedRef(ref);

  const inputOnChange = useCallback(
    (event) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const inputOnFocus = useCallback(() => {
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  const inputOnKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case 'Enter':
          if (!onSelect) {
            return;
          }
          onSelect(event.target.value);
          break;
        case 'Escape':
          onChange('');
          break;
        default:
          // return from the handler to allow the default.
          return;
      }

      event.preventDefault();
    },
    [onChange, onSelect],
  );

  const onClearClick = useCallback(() => {
    onChange('');
    inputRef.current?.focus();
  }, [inputRef, onChange]);

  return (
    <div className={className}>
      <div className="search">
        <input
          className="search-box"
          onChange={inputOnChange}
          onFocus={inputOnFocus}
          onKeyDown={inputOnKeyDown}
          placeholder={placeHolder}
          ref={inputRef}
          role="searchbox"
          type="text"
          value={value}
        />
        {value && (
          <ClickableElement onClick={onClearClick} className="clear-btn" tagName="button">
            <Close
              size={`${clearIconSizes[size]}`}
              fill={isRebrand ? colors.shadow : colors.boulder}
            />
          </ClickableElement>
        )}
        {(isRebrand || !value) && (
          <div className="search-btn">
            <MagnifyingGlass
              size={`${searchIconSizes[size]}`}
              fill={isRebrand ? colors.shadow : colors.silverSandAlt}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-func-assign
ManagedSearchBar = forwardRef(ManagedSearchBar);

ManagedSearchBar.propTypes = {
  /** Emotion styles */
  className: PropTypes.string.isRequired,
  /** Called when the text value changes. */
  onChange: PropTypes.func.isRequired,
  /** Called when the user focused on the search bar */
  onFocus: PropTypes.func,
  /** Called when the user presses enter. */
  onSelect: PropTypes.func,
  /** Size of the component (Options: `small`, `medium`, `large`) */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** The value of the search bar. */
  value: PropTypes.string.isRequired,
  /** The placeHolder value of the search bar. */
  placeHolder: PropTypes.string,
  /** Should the search bar have the rebrand styles. */
  isRebrand: PropTypes.bool,
};

ManagedSearchBar.defaultProps = {
  onFocus: null,
  onSelect: null,
  size: 'medium',
  placeHolder: 'Search',
  isRebrand: false,
};

function generateSearchBoxStyles(props) {
  const { size, isRebrand } = props;
  let height;
  let fontSize;
  let paddingLeft;
  let paddingRight = '2.2rem';

  switch (size) {
    case 'small':
      fontSize = '0.8125rem';
      height = '1.8125rem';
      paddingLeft = isRebrand ? '2.2rem' : '1.2rem';
      break;
    case 'medium':
      fontSize = '0.875rem';
      height = '2rem';
      paddingLeft = isRebrand ? '2.4rem' : '1.4rem';
      break;
    case 'large':
      fontSize = '1rem';
      height = '2.1875rem';
      paddingLeft = isRebrand ? '2.6rem' : '1.6rem';
      paddingRight = '2.3rem';
      break;
  }

  return `
    height: ${height};
    font-size: ${fontSize};
    padding-left: ${paddingLeft};
    padding-right: ${paddingRight};
  `;
}

function generateCloseButtonStyles(props) {
  const { size } = props;

  let bottom;
  switch (size) {
    case 'small':
      bottom = '0.1rem';
      break;
    case 'medium':
      bottom = '0.12rem';
      break;
    case 'large':
      bottom = '0.22rem';
      break;
  }

  return `
    bottom: ${bottom};
  `;
}

function generateSearchButtonStyles(props) {
  const { size } = props;

  let bottom;
  switch (size) {
    case 'small':
      bottom = '0';
      break;
    case 'medium':
      bottom = '0.05rem';
      break;
    case 'large':
      bottom = '0.1rem';
      break;
  }

  return `
    bottom: ${bottom};
  `;
}

const styledSearchBar = styled(ManagedSearchBar)`
  * {
    ${(props) => (props.isRebrand ? mediumInter() : medium())}
  }

  .search {
    background: ${colors.white};
    border-radius: 2.5rem;
    position: relative;

    .search-box {
      border: ${(props) => (props.isRebrand ? '0.125rem' : '0.0625rem')} solid
        ${(props) => (props.isRebrand ? colors.whiteSmoke : colors.silverSand)};
      border-radius: ${(props) => (props.isRebrand ? '0.188rem' : '2.5rem')};
      color: ${(props) => (props.isRebrand ? colors.disableGrey : colors.boulder)};
      transition: width 0.3s;
      width: 100%;

      ${generateSearchBoxStyles}

      &:focus {
        border: ${(props) => (props.isRebrand ? '0.125rem' : '0.0625rem')} solid
          ${colors.silverSandAlt};
        outline: none;
      }

      ::placeholder {
        color: ${colors.silverSandAlt};
        opacity: 1;
      }
    }

    .search-btn {
      right: ${(props) => (props.isRebrand ? 'unset' : '0.95rem')};
      left: ${(props) => (props.isRebrand ? '0.625rem' : 'unset')};
      ${generateSearchButtonStyles};
    }

    .clear-btn {
      right: 1rem;
      border: none;
      background: transparent;
      ${generateCloseButtonStyles}
      svg path {
        fill: ${colors.silverSandAlt}; /* fill attribute above does not seem to work */
      }
    }

    .search-btn,
    .clear-btn {
      padding: 0.25rem;
      position: absolute;
      transition: opacity 0.5s;
      margin: 0;
    }

    .clear-btn {
      &:hover {
        cursor: pointer;

        svg path {
          fill: ${colors.boulder};
        }
      }

      &:focus {
        opacity: 0.6;
      }
    }
  }
`;

export default styledSearchBar;
